import { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonButton,
  IonRippleEffect,
  IonTitle,
  IonContent,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  IonIcon,
  IonText,
  useIonViewDidLeave,
  useIonViewDidEnter
} from '@ionic/react';
import { chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import { Line } from 'react-chartjs-2';
import apoloClient from 'libs/apollo/apollo.lib';
import sleepReadinessStore from 'stores/sleep-readiness.store';
import hydrationFormStore from 'stores/hydrationForm.store';
import biometricReadinessStore from 'stores/biometric-readiness.store';
import acwrReadinessStore from 'stores/acwr-readiness.store';
import readinessRecoveryReportStore from 'stores/readiness-recovery.store';

import classes from './style.module.scss';
import { toJS } from 'mobx';

const DEVICE_READINESS_FIELDS = `
  id 
  date
  value 
  source
  assignedTo {
    __typename
    ... on Athlete {
      id
      weight
      user {
        id
        first_name
        last_name
      }
    } 
  }
  created_at
`;

const HYDRATION_READINESS_FIELDS = `
  id 
  date
  value: hydration_readiness
  assignedTo {
    __typename
    ... on Athlete {
      id
      weight
      user {
        id
        first_name
        last_name
      }
    } 
  }
  created_at
`;

const READINESS_RECOVERY_FIELDS = `
    id
		date
		recommendations {
			type
			base_fields
			value
		}
		recovery_type
		assignedTo {
			__typename
			... on Athlete {
				id
				weight
				user {
					id
					first_name
				}
			} 
		}
`;

const AccordionComponent = ({ title, recommendations }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className={classes.accordion}>
      <div
        className={classes.accordion__header}
        onClick={() => setIsOpen(!isOpen)}
        expand='full'
      >
        <IonText>
          <span className={classes.accordion__headerTitleMain}>{title}</span> 
          <span className={classes.accordion__headerTitleSub}>Recovery</span>
        </IonText>
        <IonRippleEffect></IonRippleEffect>
        <IonIcon  
          icon={isOpen ? chevronUpOutline : chevronDownOutline}
        />
        
      </div>
      {isOpen && (
        <ul className={classes.accordion__content}>
          {(recommendations &&
            recommendations.length > 0 )?
            recommendations.map((recommendation) => (
              <li key={recommendation}>{recommendation}</li>
            )) : <li>No recommendations</li>
          }
        </ul>
      )}
    </div>
  );
};

const ReadinessProgressPage = () => {
  const [period, setPeriod] = useState(null);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [isReadinessRecoveryLoading, setReadinessRecoveryLoading] =
    useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sleepData, setSleepData] = useState([]);
  const [biometricData, setBiometricData] = useState([]);
  const [acwrData, setAcwrData] = useState([]);
  const [hydrationData, setHydrationData] = useState([]);
  const [allDateData, setAllDateData] = useState([]);

  const sleepReadinessReports = sleepReadinessStore.getObjects();
  const hydrationReadinessReports = hydrationFormStore.getObjects();
  const biometricReadinessReports = biometricReadinessStore.getObjects();
  const acwrReadinessReports = acwrReadinessStore.getObjects();
  const readinessRecoveries = readinessRecoveryReportStore.getObjects();

  const hydrationRecoveryRecommendations = [];
  const sleepRecoveryRecommendations = [];
  const acwrRecoveryRecommendations = [];
  const biometricRecoveryRecommendations = [];

  readinessRecoveries.forEach((recovery) => {
    const recovery_type = recovery.recovery_type;
    const recommendation_values = recovery.recommendations
      .map((recommendation) => recommendation.value)
      .flat();

    switch (recovery_type) {
      case 'HYDRATION_RECOVERY':
        hydrationRecoveryRecommendations.push(...recommendation_values);
        break;
      case 'SLEEP_RECOVERY':
        sleepRecoveryRecommendations.push(...recommendation_values);
        break;
      case 'ACWR_RECOVERY':
        acwrRecoveryRecommendations.push(...recommendation_values);
        break;
      case 'BIOMETRIC_RECOVERY':
        biometricRecoveryRecommendations.push(...recommendation_values);
        break;
      default:
    }
  });

  const sleep_readiness_color = '#3964C3';
  const biometric_readiness_color = '#4E923A';
  const acwr_readiness_color = '#FFA500';
  const hydration_readiness_color = '#00FFFF';

  useIonViewDidLeave(() => {
    sleepReadinessStore.resetAllFields();
    hydrationFormStore.resetAllFields();
    biometricReadinessStore.resetAllFields();
    acwrReadinessStore.resetAllFields();
    readinessRecoveryReportStore.resetAllFields();
    setPeriod(null);
  }, []);

  useIonViewDidEnter(() => {
    setPeriod('week');
    fetchReadinessRecovery();
  }, []);

  const fetchReadinessReports = useCallback(async () => {
    setIsReportLoading(true);
    try {
      await Promise.all([
        sleepReadinessStore.searchObjectsAll(),
        hydrationFormStore.searchObjectsAll(),
        biometricReadinessStore.searchObjectsAll(),
        acwrReadinessStore.searchObjectsAll()
      ]);
    } catch (error) {
      console.log(error);
    } finally {
      setIsReportLoading(false);
    }
  }, []);

  const fetchReadinessRecovery = () => {
    readinessRecoveryReportStore.setFields(READINESS_RECOVERY_FIELDS);
    const startDate = moment();
    const endDate = moment();
    readinessRecoveryReportStore.setQuery({
      column: 'date',
      operator: 'BETWEEN',
      value: [startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD')]
    });
    setReadinessRecoveryLoading(true);
    try {
      readinessRecoveryReportStore.searchObjectsAll();
    } catch (error) {
      console.log(error);
    } finally {
      setReadinessRecoveryLoading(false);
    }
  };

  const resetChartData = () => {
    setSleepData([]);
    setBiometricData([]);
    setAcwrData([]);
    setHydrationData([]);
    setAllDateData([]);
  };

  useEffect(() => {
    let selectedDateRangeType = period;
    let andQuery = [];

    if (period) {
      let startDate = null;
      let endDate = moment();

      switch (selectedDateRangeType) {
        case 'week':
          startDate = moment().subtract(1, 'week');
          break;
        case 'month':
          startDate = moment().subtract(1, 'month');
          break;
        case 'year':
          startDate = moment().subtract(12, 'months');
          break;
        default:
          startDate = moment();
          break;
      }

      const selectedDateRangeQuery = {
        column: 'date',
        operator: 'BETWEEN',
        value: [startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD')]
      };

      const updatedDateRangeQuery = [...andQuery, selectedDateRangeQuery];
      andQuery = [...updatedDateRangeQuery];
    }
    sleepReadinessStore.setFields(DEVICE_READINESS_FIELDS);
    biometricReadinessStore.setFields(DEVICE_READINESS_FIELDS);
    acwrReadinessStore.setFields(DEVICE_READINESS_FIELDS);
    hydrationFormStore.setFields(HYDRATION_READINESS_FIELDS);

    sleepReadinessStore.setQuery({
      AND: andQuery
    });

    hydrationFormStore.setQuery({
      AND: andQuery
    });

    biometricReadinessStore.setQuery({
      AND: andQuery
    });

    acwrReadinessStore.setQuery({
      AND: andQuery
    });

    if (period) {
      fetchReadinessReports();
    }

    return () => {
      resetChartData();
    };
  }, [period, fetchReadinessReports]);

  useEffect(() => {
    if (!isReportLoading) {
      const reports = [
        ...sleepReadinessReports,
        ...hydrationReadinessReports,
        ...biometricReadinessReports,
        ...acwrReadinessReports
      ];
      const allDateData = reports.map((report) => report.date);
      const uniqueDateData = [...new Set(allDateData)].sort(
        (a, b) => moment(a, 'YYYY-MM-DD') - moment(b, 'YYYY-MM-DD')
      );
      const sleepDataDateWise = [];
      const hydrationDataDateWise = [];
      const biometricDataDateWise = [];
      const acwrDataDateWise = [];

      uniqueDateData.forEach((date) => {
        const sleepData = sleepReadinessReports.filter(
          (report) => report.date === date
        );
        const hydrationData = hydrationReadinessReports.filter(
          (report) => report.date === date
        );
        const biometricData = biometricReadinessReports.filter(
          (report) => report.date === date
        );
        const acwrData = acwrReadinessReports.filter(
          (report) => report.date === date
        );

        let sleepDataValue;
        let hydrationDataValue;
        let biometricDataValue;
        let acwrDataValue;

        if (sleepData.length > 0) {
          sleepDataValue =
            sleepData.reduce((acc, report) => acc + report.value, 0) /
            sleepData.length;
        } else {
          sleepDataValue = null;
        }

        sleepDataDateWise.push(sleepDataValue);

        if (hydrationData.length > 0) {
          hydrationDataValue =
            hydrationData.reduce((acc, report) => acc + report.value, 0) /
            hydrationData.length;
        } else {
          hydrationDataValue = null;
        }

        hydrationDataDateWise.push(hydrationDataValue);

        if (biometricData.length > 0) {
          biometricDataValue =
            biometricData.reduce((acc, report) => acc + report.value, 0) /
            biometricData.length;
        } else {
          biometricDataValue = null;
        }

        biometricDataDateWise.push(biometricDataValue);

        if (acwrData.length > 0) {
          acwrDataValue =
            acwrData.reduce((acc, report) => acc + report.value, 0) /
            acwrData.length;
        } else {
          acwrDataValue = null;
        }
        acwrDataDateWise.push(acwrDataValue);
      });
      setAllDateData(uniqueDateData);
      setSleepData(sleepDataDateWise);
      setHydrationData(hydrationDataDateWise);
      setBiometricData(biometricDataDateWise);
      setAcwrData(acwrDataDateWise);
    }
  }, [
    sleepReadinessReports,
    hydrationReadinessReports,
    biometricReadinessReports,
    acwrReadinessReports,
    isReportLoading
  ]);

  const datasets = [
    {
      label: 'Sleep',
      data: sleepData,
      backgroundColor: sleep_readiness_color,
      borderColor: sleep_readiness_color
    },
    {
      label: 'Hydration',
      data: hydrationData,
      backgroundColor: hydration_readiness_color,
      borderColor: hydration_readiness_color
    },
    {
      label: 'Biometric',
      data: biometricData,
      backgroundColor: biometric_readiness_color,
      borderColor: biometric_readiness_color
    },
    {
      label: 'ACWR',
      data: acwrData,
      backgroundColor: acwr_readiness_color,
      borderColor: acwr_readiness_color
    }
  ];

  const data = {
    labels: allDateData,
    datasets: datasets
  };

  const option = {
    spanGaps: true,
    elements: {
      point: {
        hoverRadius: 15
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: { 
          boxWidth: 30,
          boxHeight: 15,
          color: 'rgba(250, 250, 250, 0.6)',
          padding: 10, 
          generateLabels: function (chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.datasets.map(function (dataset, i) {
                return {
                  text: dataset.label,
                  fillStyle: dataset.backgroundColor,
                  hidden: !chart.isDatasetVisible(i),
                  borderRadius: 5,
                  lineCap: dataset.borderCapStyle,
                  lineDash: dataset.borderDash,
                  lineDashOffset: dataset.borderDashOffset,
                  lineJoin: dataset.borderJoinStyle,
                  lineWidth: dataset.borderWidth,
                  strokeStyle: dataset.borderColor,
                  pointStyle: dataset.pointStyle,
                  datasetIndex: i
                };
              });
            } else {
              return [];
            }
          }
        }, 
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x'
        },
        zoom: {
          wheel: {
            enabled: true
          },
          pinch: {
            enabled: true
          },
          mode: 'x'
        }
      }
    },
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1.2,
    tension: 0,
    scales: {
      x: {
        ticks: {
          display: false
        },
        grid: {
          borderColor: '#ffffff'
        }
      },
      y: {
        position: 'right',
        ticks: {
          color: 'rgba(250, 250, 250, 0.6)'
        },
        grid: {
          borderColor: 'rgba(0, 0, 0, 0)',
          tickColor: 'rgba(0, 0, 0, 0)',
          color: 'rgba(255, 255, 255, 0.2)'
        }
      }
    }
  };

  return (
    <IonPage id='readiness-progress'>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot='start'>
            <IonMenuButton></IonMenuButton>
          </IonButtons>
          <IonTitle>Reports</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className={classes.ionContent}>
        <div className={classes.reportTitle}>Readiness Report</div>
        <div className={classes.reportPeriod}>
          <IonButton
            fill='clear'
            className={period === 'year' ? classes.active : ''}
            size='small'
            onClick={() => setPeriod('year')}
          >
            Year
          </IonButton>
          <IonButton
            fill='clear'
            className={period === 'month' ? classes.active : ''}
            size='small'
            onClick={() => setPeriod('month')}
          >
            Month
          </IonButton>
          <IonButton
            fill='clear'
            className={period === 'week' ? classes.active : ''}
            size='small'
            onClick={() => setPeriod('week')}
          >
            Week
          </IonButton>
        </div>
        <IonRefresher
          slot='fixed'
          onIonRefresh={async (event) => {
            setIsRefreshing(true);
            await apoloClient.cache.reset();
            await fetchReadinessReports();
            await fetchReadinessRecovery();
            event.target.complete();
            setIsRefreshing(false);
          }}
        >
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <div className={classes.reportChart}>
          {isReportLoading && !isRefreshing && <IonSpinner />}
          <Line data={data} options={option} />
        </div>
        <div className={classes.reportRecovery}>
          <AccordionComponent
            title='Biometric'
            recommendations={biometricRecoveryRecommendations}
          />
          <AccordionComponent
            title='Hydration'
            recommendations={hydrationRecoveryRecommendations}
          />
          <AccordionComponent
            title='ACWR'
            recommendations={acwrRecoveryRecommendations}
          />
          <AccordionComponent
            title='Sleep'
            recommendations={sleepRecoveryRecommendations}
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default observer(ReadinessProgressPage);
